import { copyToClipboard } from '@choco-admin/utils';
import { Tooltip } from '@heroui/react';
import React, { useCallback, useState } from 'react';
import { FiCheck, FiCopy } from 'react-icons/fi';

import { AlertCard } from '../../../../../components/AlertCard';
import { INTEGRATION_SETTINGS_FLAG } from '../../../../../featureFlags';

/**
 * What a locked control says on hover.
 *
 * The flag is named on screen on purpose, and it is the real LaunchDarkly key,
 * not a name invented here: someone with LaunchDarkly access can search for it
 * and find it. The legacy admin told people a setting "cannot be edited" and
 * stopped there, which left them with no idea what to ask for.
 *
 * Two versions, because the control is read-only for everyone once the
 * distributor is integrated, including the people who may change it. Telling
 * someone who already holds the flag to go and ask for it is the kind of dead
 * end this tooltip exists to remove.
 */
export const LOCKED_FIELD_TOOLTIP = `Read-only for you. To edit this, ask for the ${INTEGRATION_SETTINGS_FLAG} feature flag to be switched on for you.`;

export const LOCKED_FIELD_TOOLTIP_FOR_EDITOR =
  'Read-only here. Change it under Integration settings on the Settings tab.';

export const RequestAccess: React.FC = () => {
  const [justCopied, setJustCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    if (await copyToClipboard(INTEGRATION_SETTINGS_FLAG)) {
      setJustCopied(true);
      window.setTimeout(() => setJustCopied(false), 2000);
    }
  }, []);

  return (
    <details className="group rounded-medium border border-default-200 bg-content1 px-3 py-2">
      <summary className="cursor-pointer text-small font-medium text-foreground marker:content-none">
        How to get access
      </summary>
      <div className="mt-2 space-y-2">
        <p className="text-small text-default-600">
          Ask someone with LaunchDarkly access to switch this feature flag on
          for you. Copy the name into your message so they switch on the right
          one.
        </p>
        <button
          type="button"
          onClick={handleCopy}
          data-testid="integration-flag-copy"
          aria-label={`Copy the feature flag name ${INTEGRATION_SETTINGS_FLAG}`}
          className="inline-flex items-center gap-2 rounded-md bg-default-100 px-2 py-1 text-default-700 transition-colors hover:bg-default-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
        >
          <span className="font-mono text-xs">{INTEGRATION_SETTINGS_FLAG}</span>
          {justCopied ? (
            <FiCheck size={12} className="text-success" aria-hidden="true" />
          ) : (
            <FiCopy size={12} aria-hidden="true" />
          )}
        </button>
        {/* No names here. Who can switch a flag on is not something this app
            knows, and inventing a team or a channel is worse than leaving the
            reader to ask the person they would normally ask. */}
      </div>
    </details>
  );
};

interface IntegrationLockNoticeProps {
  title: string;
  message: string;
}

/**
 * Why a group of settings is read-only, and how to stop it being read-only.
 *
 * Shown only to someone who cannot edit them. Someone who can gets a warning
 * instead, which needs no request.
 */
export const IntegrationLockNotice: React.FC<IntegrationLockNoticeProps> = ({
  title,
  message,
}) => (
  <div className="space-y-2">
    <AlertCard type="info" title={title} message={message} />
    <RequestAccess />
  </div>
);

interface LockedFieldTooltipProps {
  isLocked: boolean;
  /**
   * Whether this person may change the setting, which decides what the tooltip
   * tells them to do rather than whether it appears. Both are read-only here.
   */
  canChange?: boolean;
  /**
   * What to say instead, for a control locked by something other than the
   * integration flag. It replaces both versions above.
   */
  reason?: string;
  children: React.ReactNode;
}

/**
 * Explains a single read-only control on hover.
 *
 * Two details, both learned the hard way:
 *
 * - The tooltip sits on a wrapper rather than on the control, because a disabled
 *   input emits no pointer events and a tooltip attached to one never opens. The
 *   legacy admin wrapped its disabled checkbox for the same reason.
 * - The same sentence is repeated in a visually hidden span, because a tooltip
 *   that only opens on hover is not there at all for anyone reading the page with
 *   a screen reader or driving it from the keyboard.
 */
export const LockedFieldTooltip: React.FC<LockedFieldTooltipProps> = ({
  isLocked,
  canChange = false,
  reason: reasonOverride,
  children,
}) => {
  if (!isLocked) return <>{children}</>;

  const reason =
    reasonOverride ??
    (canChange ? LOCKED_FIELD_TOOLTIP_FOR_EDITOR : LOCKED_FIELD_TOOLTIP);

  return (
    <Tooltip content={reason} placement="left" className="max-w-xs">
      <div className="cursor-help">
        {children}
        <span className="sr-only">{reason}</span>
      </div>
    </Tooltip>
  );
};
